import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { invitesAPI } from '../lib/api';
import { useAuthStore } from '../stores/authStore';

function formatDate(d) {
  return new Date(d).toLocaleDateString(undefined, { day: 'numeric', month: 'long', year: 'numeric' });
}

// Where an invite link lands. It explains the invite before anyone signs in,
// carries the token through sign-in or sign-up, and accepts it as the signed-in
// person. The server only accepts it from an account with the invited address.
export default function InviteAccept() {
  const { token } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const session = useAuthStore(state => state.token);
  const user = useAuthStore(state => state.user);
  const setAuth = useAuthStore(state => state.setAuth);
  const logout = useAuthStore(state => state.logout);

  const [invite, setInvite] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [accepting, setAccepting] = useState(false);

  useEffect(() => {
    invitesAPI.lookup(token)
      .then(r => setInvite(r.data))
      .catch(err => {
        // Straight back from signing up through Google: the server applied the
        // invite while creating the account, which is why the link is spent.
        if (params.get('after') === 'signin' && useAuthStore.getState().token) {
          navigate('/', { replace: true });
          return;
        }
        setError(err.response?.data?.error || 'This invite link is not valid.');
      })
      .finally(() => setLoading(false));
  }, [token]);

  async function accept() {
    setAccepting(true);
    setError('');
    try {
      const r = await invitesAPI.accept(token);
      setAuth(r.data.token, r.data.user);
      navigate('/', { replace: true });
    } catch (err) {
      setError(err.response?.data?.error || 'Could not accept the invite.');
      setAccepting(false);
    }
  }

  const shell = (children) => (
    <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center px-4">
      <div className="bg-[#111111] border border-[#2a2a2a] rounded-lg w-full max-w-md p-6">{children}</div>
    </div>
  );

  if (loading) return shell(<div className="text-gray-400 text-sm text-center py-8">Loading…</div>);

  if (!invite) {
    return shell(
      <>
        <h1 className="text-white font-semibold mb-2">Invite unavailable</h1>
        <p className="text-sm text-red-400 mb-4">{error}</p>
        <Link to="/" className="text-sm text-blue-400 hover:text-blue-300">Go to Mission Control</Link>
      </>
    );
  }

  const isMember = invite.kind === 'member';
  const wrongAccount = session && user?.email && user.email.toLowerCase() !== invite.email.toLowerCase();
  const loginUrl = (mode) => `/login?invite=${encodeURIComponent(token)}${mode ? `&mode=${mode}` : ''}`;

  return shell(
    <>
      <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
        {isMember ? 'Join an account' : 'Guest access'}
      </div>
      <h1 className="text-white font-semibold text-lg mb-1">
        {invite.invited_by ? `${invite.invited_by} invited you` : 'You have been invited'}
      </h1>
      <p className="text-sm text-gray-400 mb-4">
        {isMember ? (
          <>to join <span className="text-white">{invite.organisation_name}</span> as {invite.role === 'admin' ? 'an admin' : 'a member'}.</>
        ) : (
          <>to work in <span className="text-white">{invite.workspace_name}</span>, a workspace in {invite.organisation_name}.</>
        )}
      </p>

      <div className="text-xs text-gray-500 leading-relaxed mb-5 space-y-2">
        {isMember ? (
          <p>
            Members can open every workspace in the account, build and run shows, and invite guests.
            {invite.role === 'admin' && ' As an admin you can also manage members and create workspaces.'}
          </p>
        ) : (
          <>
            <p>As a guest you can open this workspace&rsquo;s shows, fill in scripts, notes and graphics text, and run the show.</p>
            <p>
              If you pay for a Mission Control account of your own, you also get full production access to its
              shows — running order, timings and automation.
            </p>
            {invite.guest_expires_at && <p>Your access ends on {formatDate(invite.guest_expires_at)}.</p>}
          </>
        )}
        <p>Sent to <span className="text-gray-300">{invite.email}</span>.</p>
      </div>

      {invite.expired ? (
        <p className="text-sm text-red-400">
          This invite has expired. Ask {invite.invited_by || 'whoever sent it'} for a new one.
        </p>
      ) : !session ? (
        <div className="flex gap-2">
          <button
            onClick={() => navigate(loginUrl())}
            className="flex-1 px-3 py-2 bg-blue-700 hover:bg-blue-600 text-white text-sm font-medium rounded transition-colors"
          >
            Sign in to accept
          </button>
          <button
            onClick={() => navigate(loginUrl('register'))}
            className="flex-1 px-3 py-2 bg-[#1a1a1a] hover:bg-[#222] border border-[#2a2a2a] text-gray-300 text-sm rounded transition-colors"
          >
            Create an account
          </button>
        </div>
      ) : wrongAccount ? (
        <div className="space-y-3">
          <p className="text-sm text-amber-400 leading-relaxed">
            You&rsquo;re signed in as {user.email}. This invite can only be accepted by {invite.email}.
          </p>
          <button
            onClick={logout}
            className="px-3 py-2 bg-[#1a1a1a] hover:bg-[#222] border border-[#2a2a2a] text-gray-300 text-sm rounded transition-colors"
          >
            Sign out
          </button>
        </div>
      ) : (
        <button
          onClick={accept}
          disabled={accepting}
          className="w-full px-3 py-2 bg-blue-700 hover:bg-blue-600 disabled:opacity-40 text-white text-sm font-medium rounded transition-colors"
        >
          {accepting ? 'Accepting…' : 'Accept invite'}
        </button>
      )}

      {error && <p className="text-xs text-red-400 mt-3">{error}</p>}
    </>
  );
}
